<template>
  <div class="salary-anomaly-addon">
    <div class="addon-head">
      <div>
        <span>HRM_SALARY_ANOMALY_INSIGHT</span>
        <h2>薪酬结构异常洞察</h2>
        <p>聚合薪资明细、扣款结构和部门薪酬分布，识别薪资计算偏差、扣款异常和薪资数据质量缺口。</p>
      </div>
      <el-button type="primary" icon="el-icon-refresh" :loading="loading" @click="loadData">刷新</el-button>
    </div>

    <el-row :gutter="16" class="metric-row">
      <el-col v-for="item in metrics" :key="item.label" :xs="24" :sm="12" :lg="6">
        <div class="metric-card" :class="item.tone">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
          <em>{{ item.meta }}</em>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="16">
      <el-col :xs="24" :lg="14">
        <el-card shadow="never">
          <div slot="header">薪资计算偏差排行</div>
          <el-table :data="overview.salaryGapRanking" size="small" v-loading="loading">
            <el-table-column prop="staff_name" label="员工" width="110" />
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="month" label="月份" width="90" />
            <el-table-column prop="total_salary" label="实发" width="100" />
            <el-table-column prop="expected_salary" label="推算" width="100" />
            <el-table-column prop="gap_amount" label="偏差" width="100" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="10">
        <el-card shadow="never">
          <div slot="header">月度薪酬趋势</div>
          <div class="trend-list">
            <div v-for="item in overview.monthTrend" :key="item.month" class="trend-item">
              <span>{{ item.month }}</span>
              <strong>{{ formatMoney(item.total_payroll) }}</strong>
              <em>均薪 {{ formatMoney(item.avg_total_salary) }} / {{ item.salary_count }} 条</em>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="lower-row">
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">部门薪酬分布</div>
          <el-table :data="overview.deptSalaryDistribution" size="small" v-loading="loading">
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="salary_count" label="记录" width="80" />
            <el-table-column prop="avg_base_salary" label="平均基本" width="100" />
            <el-table-column prop="avg_total_salary" label="平均实发" width="100" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">扣款异常排行</div>
          <el-table :data="overview.deductRiskRanking" size="small" v-loading="loading">
            <el-table-column prop="staff_name" label="员工" width="110" />
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="month" label="月份" width="90" />
            <el-table-column prop="total_deduct" label="总扣款" width="100" />
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <el-card class="lower-row" shadow="never">
      <div slot="header">薪资字段缺口</div>
      <el-table :data="overview.missingFieldList" size="small" v-loading="loading">
        <el-table-column prop="staff_name" label="员工" width="120" />
        <el-table-column prop="dept_name" label="部门" min-width="160" />
        <el-table-column prop="month" label="月份" width="100" />
        <el-table-column prop="missing_base_salary" label="基本工资" width="100" :formatter="formatFlag" />
        <el-table-column prop="missing_total_salary" label="总工资" width="100" :formatter="formatFlag" />
        <el-table-column prop="missing_month" label="月份字段" width="100" :formatter="formatFlag" />
      </el-table>
    </el-card>
  </div>
</template>

<script>
import { getSalaryAnomalyInsightOverview } from '../api/salaryAnomalyInsight'

export default {
  name: 'SalaryAnomalyInsightOverview',
  data () {
    return {
      loading: false,
      overview: {
        salaryRecordCount: 0,
        staffCovered: 0,
        monthCovered: 0,
        missingTotalSalary: 0,
        negativeNetRisk: 0,
        deductRuleCount: 0,
        salaryGapRanking: [],
        deptSalaryDistribution: [],
        deductRiskRanking: [],
        monthTrend: [],
        missingFieldList: []
      }
    }
  },
  computed: {
    metrics () {
      return [
        { label: '薪资记录', value: this.overview.salaryRecordCount, meta: `${this.overview.staffCovered || 0} 名员工` },
        { label: '覆盖月份', value: this.overview.monthCovered, meta: '历史薪酬周期', tone: 'blue' },
        { label: '总薪缺口', value: this.overview.missingTotalSalary, meta: 'total_salary 为空', tone: 'orange' },
        { label: '净薪风险', value: this.overview.negativeNetRisk, meta: `${this.overview.deductRuleCount || 0} 条扣款规则`, tone: 'red' }
      ]
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    loadData () {
      this.loading = true
      getSalaryAnomalyInsightOverview().then(response => {
        if (response.code === 200) {
          this.overview = Object.assign({}, this.overview, response.data || {})
        } else {
          this.$message.warning(response.message || '薪酬结构异常洞察加载失败')
        }
      }).finally(() => {
        this.loading = false
      })
    },
    formatFlag (row, column, value) {
      return Number(value) === 1 ? '缺失' : '完整'
    },
    formatMoney (value) {
      return Number(value || 0).toFixed(2)
    }
  }
}
</script>

<style lang="less" scoped>
.salary-anomaly-addon {
  padding: 20px;
  background: #f5f7fb;
  min-height: 100%;
}

.addon-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 22px 24px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.addon-head span {
  display: block;
  margin-bottom: 8px;
  color: #8a5a00;
  font-size: 12px;
  font-weight: 700;
}

.addon-head h2 {
  margin: 0 0 8px;
  color: #17233d;
  font-size: 24px;
}

.addon-head p {
  margin: 0;
  color: #5f6f89;
}

.metric-row {
  margin-bottom: 16px;
}

.metric-card {
  min-height: 118px;
  padding: 18px;
  margin-bottom: 16px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.metric-card span,
.metric-card em {
  display: block;
  color: #6b7a90;
  font-style: normal;
}

.metric-card strong {
  display: block;
  margin: 10px 0 8px;
  color: #17233d;
  font-size: 30px;
  line-height: 1;
}

.metric-card.red strong {
  color: #d93026;
}

.metric-card.orange strong {
  color: #d9822b;
}

.metric-card.blue strong {
  color: #2f6fec;
}

.lower-row {
  margin-top: 16px;
}

.trend-list {
  display: grid;
  gap: 10px;
}

.trend-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #edf1f7;
  border-radius: 6px;
}

.trend-item span,
.trend-item em {
  color: #5f6f89;
  font-style: normal;
}

.trend-item strong {
  color: #17233d;
  font-size: 16px;
}
</style>
